import { all, call, put, takeLatest } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import isFunction from "lodash/isFunction";

import { showError } from "utils/notifications";
import { downloadCSVFromString } from "utils/downloadCSVFromString";

import request from "../../api";

import { IPayloadAction } from "../rootInterface";
import { TTransfersFilter } from "../transfers/types";
import { openModal } from "../modals/actions";
import { EModals } from "../modals/types";

import {
  TCreateCustomEntityCategoryOptions,
  TCreateCustomLabelOptions,
  TCustomLabelCreateOptions,
  TCustomLabelsInfoOptions,
  TLabelsEntityCategoryOptions,
  TLabelsGroup,
  TLabelsGroupDeleteOptions,
  TLabelsGroupEditOptions,
  TLabelsGroupListOptions,
  TUpdatePriorityOptions,
  TLabelsEntityCategoryData,
  TLabelsGroupListData,
  TLabelsGroupImportCSVOptions,
} from "./types";
import {
  createCustomEntityCategoryFailure,
  createCustomEntityCategoryRequest,
  createCustomEntityCategorySuccess,
  createCustomLabelFailure,
  createCustomLabelRequest,
  createCustomLabelSuccess,
  deleteCustomLabelFailure,
  deleteCustomLabelRequest,
  deleteCustomLabelSuccess,
  deleteEntityCategoryFailure,
  deleteEntityCategoryRequest,
  deleteEntityCategorySuccess,
  editCustomEntityCategoryFailure,
  editCustomEntityCategoryRequest,
  editCustomEntityCategorySuccess,
  editCustomLabelFailure,
  editCustomLabelRequest,
  editCustomLabelSuccess,
  entityCategoriesOverviewFailure,
  entityCategoriesOverviewRequest,
  entityCategoriesOverviewSuccess,
  labelGroupCreateFailure,
  labelGroupCreateRequest,
  labelGroupCreateSuccess,
  labelsEntityCategoryFailure,
  labelsEntityCategoryRequest,
  labelsEntityCategorySuccess,
  labelsGroupDeleteFailure,
  labelsGroupDeleteRequest,
  labelsGroupDeleteSuccess,
  labelsGroupFailure,
  labelsGroupInfoFailure,
  labelsGroupInfoRequest,
  labelsGroupInfoSuccess,
  labelsGroupLabelsEditFailure,
  labelsGroupLabelsEditRequest,
  labelsGroupLabelsEditSuccess,
  labelsGroupListFailure,
  labelsGroupListRequest,
  labelsGroupListSuccess,
  labelsGroupRequest,
  labelsGroupSuccess,
  updatePriorityFailure,
  updatePriorityRequest,
  updatePrioritySuccess,
  importCSVRequest,
  importCSVSuccess,
  importCSVFailure,
  importCSVRequestClean,
  labelsGroupExportCSVRequest,
  labelsGroupExportCSVSuccess,
  labelsGroupExportCSVFailure,
} from "./reducers";

function* labelsGroupSaga() {
  try {
    const response: AxiosResponse = yield call(request.get, "/labels/groups/");
    yield put(labelsGroupSuccess(response.data));
  } catch (error) {
    yield put(labelsGroupFailure(error));
  }
}

function* labelsGroupInfoSaga(action: IPayloadAction<TCustomLabelsInfoOptions>) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.get, `/labels/groups/${id}/`);
    yield put(labelsGroupInfoSuccess(response.data));
  } catch (error) {
    yield put(labelsGroupInfoFailure(error));
  }
}

function* labelsGroupCreate(action: IPayloadAction<TCustomLabelCreateOptions>) {
  const { data } = action.payload;
  try {
    const response: AxiosResponse<TLabelsGroup> = yield call(request.post, "/labels/groups/create/", data);
    yield put(labelGroupCreateSuccess(response.data));
    yield call(labelsGroupSaga);
    if (isFunction(action.payload?.callOnSuccess)) action.payload.callOnSuccess(response.data);
  } catch (error) {
    yield put(labelGroupCreateFailure(error));
  }
}

function* labelsGroupDeleteSaga(action: IPayloadAction<TLabelsGroupDeleteOptions>) {
  const { id, callOnSuccess } = action.payload;
  try {
    yield call(request.delete, `/labels/groups/${id}/delete/`);
    yield put(labelsGroupDeleteSuccess());
    yield call(labelsGroupSaga);

    if (isFunction(callOnSuccess)) callOnSuccess();
  } catch (error) {
    yield put(labelsGroupDeleteFailure(error));
  }
}

function* labelsGroupLabelsEditSaga(action: IPayloadAction<TLabelsGroupEditOptions>) {
  const label = action.payload;
  try {
    const response: AxiosResponse<TLabelsGroup> = yield call(request.post, `/labels/groups/${label?.id}/edit/`, label);
    yield put(labelsGroupLabelsEditSuccess(response.data));

    if (isFunction(label?.callOnSuccess)) label?.callOnSuccess(response.data);
  } catch (error) {
    yield put(labelsGroupLabelsEditFailure(error));
  }
}

function* labelsGroupListSaga(action: IPayloadAction<TLabelsGroupListOptions>) {
  const { id, pagination } = action.payload;
  const keys = action.payload.filter as TTransfersFilter;
  const { search, entity_category } = keys;
  const network = keys.blockchain;

  try {
    const response: AxiosResponse<TLabelsGroupListData> = yield call(request.get, `/labels/groups/${id}/labels/`, {
      params: {
        size: pagination?.size,
        page: pagination?.page,
        entity_category,
        network,
        search,
      },
    });
    yield put(labelsGroupListSuccess({ data: response.data, infiniteScroll: action.payload.infiniteScroll }));
  } catch (error) {
    yield put(labelsGroupListFailure(error));
  }
}

function* labelsEntityCategorySaga(action: IPayloadAction<TLabelsEntityCategoryOptions>) {
  const { pagination, is_custom,extras,order_by,search } = action.payload;
  try {
    const response: AxiosResponse<TLabelsEntityCategoryData> = yield call(request.get, `/entity-categories/`, {
      params: {
        size: pagination?.size,
        page: pagination?.page,
        is_custom,
        extras,
        order_by,
        search,
      },
    });
    yield put(labelsEntityCategorySuccess({ data: response.data, infiniteScroll: action.payload.infiniteScroll }));
    if (isFunction(action.payload?.callOnSuccess)) action.payload.callOnSuccess(response.data);
  } catch (error) {
    yield put(labelsEntityCategoryFailure(error));
  }
}

function* createCustomLabelSaga(action: IPayloadAction<TCreateCustomLabelOptions>) {
  const { data, callOnSuccess } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.post, "/labels/create/", data);
    yield put(createCustomLabelSuccess(response.data));
    if (isFunction(callOnSuccess)) callOnSuccess(response.data);
  } catch (error) {
    yield put(createCustomLabelFailure(error));
    if (error?.response?.data?.code) {
      showError({ message:error?.response?.data?.code });
    }
  }
}

function* editCustomLabelSaga(action: IPayloadAction<TCreateCustomLabelOptions & { id: string }>) {
  const { id, data, callOnSuccess } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.post, `/labels/${id}/edit/`, data);
    yield put(editCustomLabelSuccess({ ...data, id }));

    if (isFunction(callOnSuccess)) callOnSuccess(response.data);
  } catch (error) {
    yield put(editCustomLabelFailure(error));
    if (error?.response?.data?.code) {
      showError({ message:error?.response?.data?.code });
    }
  }
}

function* deleteCustomLabelSaga(action: IPayloadAction<{ id: string; callOnSuccess?: () => void }>) {
  const { id, callOnSuccess } = action.payload;
  try {
    yield call(request.delete, `/labels/${id}/delete/`);
    yield put(deleteCustomLabelSuccess());
    if (isFunction(callOnSuccess)) callOnSuccess();
  } catch (error) {
    yield put(deleteCustomLabelFailure(error));
  }
}

function* deleteEntityCategorySaga(action: IPayloadAction<{ id: string; callOnSuccess?: () => void }>) {
  const { id, callOnSuccess } = action.payload;
  try {
    yield call(request.delete, `/entity-categories/${id}/delete/`);
    yield put(deleteEntityCategorySuccess({ id }));
    if (isFunction(callOnSuccess)) callOnSuccess();
  } catch (error) {
    yield put(deleteEntityCategoryFailure(error));
  }
}

function* createCustomEntityCategorySaga(action: IPayloadAction<TCreateCustomEntityCategoryOptions>) {
  const { data, callOnSuccess } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.post, "/entity-categories/create/", data);
    yield put(createCustomEntityCategorySuccess(response.data));
    if (isFunction(callOnSuccess)) callOnSuccess(response.data);
  } catch (error) {
    yield put(createCustomEntityCategoryFailure(error));
  }
}

function* editCustomEntityCategorySaga(action: IPayloadAction<TCreateCustomEntityCategoryOptions & { id: string }>) {
  const { id, data, callOnSuccess } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.post, `/entity-categories/${id}/edit/`, data);
    yield put(editCustomEntityCategorySuccess(response.data));
    if (isFunction(callOnSuccess)) callOnSuccess(response.data);
  } catch (error) {
    yield put(editCustomEntityCategoryFailure(error));
  }
}

function* entityCategoriesOverviewSaga() {
  try {
    const response: AxiosResponse = yield call(request.get, "/labels/overview/");
    yield put(entityCategoriesOverviewSuccess(response.data));
  } catch (error) {
    yield put(entityCategoriesOverviewFailure(error));
  }
}

function* updatePrioritySaga(action: IPayloadAction<TUpdatePriorityOptions>) {
  const { id, priority } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.post, `/labels/groups/${id}/update-priority/`, { priority });
    yield put(updatePrioritySuccess(response.data));
    yield call(labelsGroupSaga);
  } catch (error) {
    yield put(updatePriorityFailure(error));
  }
}

function* importCSVCustomLabelSaga(action: IPayloadAction<TLabelsGroupImportCSVOptions>) {
  const { callOnSuccess, id, data, rowCount } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.post, `/labels/groups/${id}/import-csv/`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    yield put(importCSVSuccess(response.data));
    if (isFunction(callOnSuccess)) callOnSuccess();
  } catch (error) {
    if (error.response && error.response.status === 400) {
      const fileStr = error.response.data?.file;
      if (fileStr) {
        yield put(importCSVFailure(fileStr));
      } else {
        yield put(importCSVFailure(error.response.data));
        yield put(
          openModal({ modal: EModals.customLabelsDetailCSVErrors, data: { errorList: error.response.data, rowCount } })
        );
      }
    } else {
      yield put(importCSVRequestClean());
      showError({ message:error?.message });
    }
  }
}

function* labelsGroupExportCSVSaga(action: IPayloadAction<TLabelsGroupEditOptions>) {
  const { id, name } = action.payload;
  try {
    const response: AxiosResponse = yield call(request.get, `/labels/groups/${id}/export-csv/`);
    downloadCSVFromString(response.data, `${name}.csv`);
    yield put(labelsGroupExportCSVSuccess(response.data));
  } catch (error) {
    yield put(labelsGroupExportCSVFailure(error));
    if (error?.response?.data?.code) {
      showError({ message:error?.response?.data?.code });
    }
  }
}

function* Saga(): Generator {
  yield all([
    takeLatest(labelsGroupRequest.type, labelsGroupSaga),
    takeLatest(labelsGroupInfoRequest.type, labelsGroupInfoSaga),
    takeLatest(labelGroupCreateRequest.type, labelsGroupCreate),
    takeLatest(labelsGroupDeleteRequest.type, labelsGroupDeleteSaga),
    takeLatest(labelsGroupLabelsEditRequest.type, labelsGroupLabelsEditSaga),
    takeLatest(labelsGroupListRequest.type, labelsGroupListSaga),
    takeLatest(createCustomLabelRequest.type, createCustomLabelSaga),
    takeLatest(labelsEntityCategoryRequest.type, labelsEntityCategorySaga),
    takeLatest(editCustomLabelRequest.type, editCustomLabelSaga),
    takeLatest(deleteCustomLabelRequest.type, deleteCustomLabelSaga),
    takeLatest(deleteEntityCategoryRequest.type, deleteEntityCategorySaga),
    takeLatest(createCustomEntityCategoryRequest.type, createCustomEntityCategorySaga),
    takeLatest(editCustomEntityCategoryRequest.type, editCustomEntityCategorySaga),
    takeLatest(entityCategoriesOverviewRequest.type, entityCategoriesOverviewSaga),
    takeLatest(updatePriorityRequest.type, updatePrioritySaga),
    takeLatest(importCSVRequest.type, importCSVCustomLabelSaga),
    takeLatest(labelsGroupExportCSVRequest.type, labelsGroupExportCSVSaga),
  ]);
}

export default Saga;
